import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Download, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtDate } from "@/lib/format";
import { downloadFile } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const uniq = (a) => [...new Set(a.filter(Boolean))].sort();
const Filter = ({ value, onChange, all, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-52 bg-white text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="all">{all}</SelectItem>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

const CompanyBlock = ({ code, rows }) => (
  <>
    <tr className="border-t-2 border-foreground bg-muted/60" data-testid={`pl-summary-company-${code}`}>
      <td className="px-3 py-2 font-semibold" colSpan={4}>{code}</td>
      <td className="px-3 py-2 text-xs" colSpan={3}>{rows.length} commesse · {uniq(rows.map((r) => r.customer)).length} committenti · {uniq(rows.map((r) => r.manager)).length} gestori</td>
      <td className="px-3 py-2 text-right font-mono font-semibold" data-testid={`pl-summary-items-${code}`}>{rows.reduce((s, r) => s + r.items, 0)}</td>
      <td colSpan={2} />
    </tr>
    {rows.map((r) => (
      <tr key={r.project_id} className="border-t text-xs" data-testid={`pl-summary-row-${r.code}`}>
        <td className="px-3 py-1.5 font-mono font-semibold">{r.code}</td>
        <td className="max-w-[260px] truncate" title={r.name}>{r.name}</td>
        <td>{r.customer}</td><td>{r.manager || "—"}</td><td>{r.regions.join(", ")}</td><td>{r.services.join(", ")}</td>
        <td>{r.status}</td>
        <td className={`px-3 text-right font-mono ${r.items ? "" : "text-red-700"}`}>{r.items || "nessun listino in vigore"}</td>
        <td className="whitespace-nowrap">{r.valid_from ? `${fmtDate(r.valid_from)} → ${r.valid_to ? fmtDate(r.valid_to) : "∞"}` : "—"}</td>
        <td className="px-3 text-right font-mono">{r.versions}</td>
      </tr>
    ))}
  </>
);

export const PriceListSummary = () => {
  const { companyId } = useData();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [cust, setCust] = useState("all");
  const [mgr, setMgr] = useState("all");
  useEffect(() => { if (open) { api.get("/price-lists/summary", { params: { company_id: companyId || undefined } }).then((r) => setRows(r.data)).catch(() => setRows([])); } }, [open, companyId]);
  const shown = (rows || []).filter((r) => (cust === "all" || r.customer === cust) && (mgr === "all" || (r.manager || "—") === mgr));
  const companies = uniq(shown.map((r) => r.company_code));
  return (
    <div className="mb-6 border bg-white" data-testid="pl-summary">
      <button type="button" className="flex w-full items-center gap-2 px-4 py-3 text-left" onClick={() => setOpen(!open)} data-testid="pl-summary-toggle">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}<Layers className="h-4 w-4" />
        <span className="font-semibold">Riepilogo listini per società</span>
        {rows && <span className="text-xs text-muted-foreground">{rows.length} commesse · {rows.reduce((s, r) => s + r.items, 0)} voci in vigore</span>}
      </button>
      {open && rows && (
        <div className="border-t">
          <div className="flex flex-wrap items-center gap-2 px-4 py-2">
            <Filter value={cust} onChange={setCust} all="Tutti i committenti" options={uniq(rows.map((r) => r.customer))} testId="pl-summary-customer" />
            <Filter value={mgr} onChange={setMgr} all="Tutti i gestori" options={uniq(rows.map((r) => r.manager || "—"))} testId="pl-summary-manager" />
            <Button size="sm" variant="outline" className="ml-auto" onClick={() => downloadFile(`/price-lists/summary.xlsx${companyId ? `?company_id=${companyId}` : ""}`, "riepilogo_listini.xlsx")} data-testid="pl-summary-xlsx"><Download className="mr-1 h-3.5 w-3.5" />Excel</Button>
          </div>
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white text-left text-[10px] uppercase text-muted-foreground"><tr>
                <th className="px-3 py-1.5">Commessa</th><th>Descrizione</th><th>Committente</th><th>Gestore</th><th>Regione</th><th>Attività</th><th>Stato</th><th className="px-3 text-right">Voci</th><th>Validità</th><th className="px-3 text-right">Versioni</th></tr></thead>
              <tbody>{companies.map((c) => <Fragment key={c}><CompanyBlock code={c} rows={shown.filter((r) => r.company_code === c)} /></Fragment>)}</tbody>
            </table>
            {!shown.length && <p className="px-4 py-3 text-sm text-muted-foreground" data-testid="pl-summary-empty">Nessuna commessa.</p>}
          </div>
        </div>
      )}
    </div>
  );
};
